import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-model/views/default-catalog-products-views";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode, useContext } from "react";
import { matchPath, Navigate } from "react-router-dom";

const supplierDetailsBreadcrumb = breadcrumbSlot();
const purchaseOrderBreadcrumb = breadcrumbSlot();
const purchaseOrderViewBreadcrumb = breadcrumbSlot();
const shipmentViewBreadcrumb = breadcrumbSlot();
const shipmentDetailBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();
const inventoryLevelsViewBreadcrumb = breadcrumbSlot();
const forecastingViewBreadcrumb = breadcrumbSlot();
const catalogProductsViewBreadcrumb = breadcrumbSlot();
const billOfMaterialsDetailBreadcrumb = breadcrumbSlot();
const transferViewBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/catalog/products",
  "/catalog/products/:viewName",
  "/catalog/products/:viewName/:productFamilyId",
  "/catalog/products/:viewName/:productFamilyId/:productId",
  "/catalog/products/:viewName/:productFamilyId/:productId/locations/:locationId",
  "/catalog/consignment",
  "/catalog/consigned-product-families/:productFamilyId",
  "/pricing-rules",
  "/adjustments",
  "/products",
  "/products/:viewName",
  "/liquidation/products",
  "/suppliers",
  "/suppliers/:supplierId",
  "/purchase-orders",
  "/purchase-orders/:purchaseOrderId",
  "/shipments",
  "/shipments/damaged",
  "/shipments/:viewName",
  "/shipments/:viewName/:shipmentId",
  "/forecasting",
  "/forecasting/:viewName",
  "/scenario-planning",
  "/scenario-planning/:scenarioId",
  "/managed-returns",
  "/managed-returns/returns-processing",
  "/managed-returns/refulfillment",
  "/consignment",
  "/bill-of-materials",
  "/bill-of-materials/new",
  "/bill-of-materials/:billOfMaterialsId",
  "/transfers",
  "/transfers/:viewName",
  "/transfers/:viewName/new",
  "/transfers/:viewName/:transferId",
];

function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [];

const HIDE_HEADER_ROUTES: string[] = [...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/inventory${route}`, url),
  );
}

export const inventoryRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Inventory" },
    async lazy() {
      const { Page } = await import("../../page");
      const { ImsStatusWrapper } = await import(
        "@redotech/redo-merchant-inventory-app/components/ims-status-wrapper"
      );
      return {
        element: (
          <ImsStatusWrapper>
            <Page
              hideHeader={hideHeader}
              hideNavbar={hideNav}
              hidePadding={hidePadding}
            />
          </ImsStatusWrapper>
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="products/all" /> },
      {
        path: "products",
        handle: { breadcrumb: "Inventory Levels" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: ":viewName",
            handle: { breadcrumb: inventoryLevelsViewBreadcrumb },
            async lazy() {
              const { InventoryProductsWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/inventory-products/inventory-products"
              );
              return {
                element: (
                  <InventoryProductsWrapper
                    breadcrumb={inventoryLevelsViewBreadcrumb}
                  />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { InventoryProductsPage } = await import(
                    "@redotech/redo-merchant-inventory-app/inventory-products/inventory-products"
                  );
                  return { Component: InventoryProductsPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "catalog",
        handle: { breadcrumb: "Catalog" },
        children: [
          { index: true, element: <Navigate replace to="products" /> },
          {
            path: "products",
            handle: { breadcrumb: "Products" },
            children: [
              {
                index: true,
                element: <Navigate replace to={DEFAULT_CATALOG_VIEW} />,
              },
              {
                path: ":viewName",
                handle: { breadcrumb: catalogProductsViewBreadcrumb },
                async lazy() {
                  const { CatalogProductsWrapper } = await import(
                    "@redotech/redo-merchant-inventory-app/catalog/catalog-products-wrapper"
                  );
                  return {
                    element: (
                      <CatalogProductsWrapper
                        breadcrumb={catalogProductsViewBreadcrumb}
                        productBreadcrumb={productDetailBreadcrumb}
                        productFamilyBreadcrumb={productFamilyDetailBreadcrumb}
                      />
                    ),
                  };
                },
                children: [
                  {
                    index: true,
                    async lazy() {
                      const { CatalogProducts } = await import(
                        "@redotech/redo-merchant-inventory-app/catalog-products"
                      );
                      return { Component: CatalogProducts };
                    },
                  },
                  {
                    path: ":productFamilyId",
                    handle: { breadcrumb: productFamilyDetailBreadcrumb },
                    children: [
                      {
                        index: true,
                        async lazy() {
                          const { ProductFamilyView } = await import(
                            "@redotech/redo-merchant-inventory-app/product-families/product-family-view"
                          );
                          return { Component: ProductFamilyView };
                        },
                      },
                      {
                        path: ":productId",
                        handle: { breadcrumb: productDetailBreadcrumb },
                        children: [
                          {
                            index: true,
                            async lazy() {
                              const { ProductView } = await import(
                                "@redotech/redo-merchant-inventory-app/products/product-view"
                              );
                              return { Component: ProductView };
                            },
                          },
                          {
                            path: "locations/:locationId",
                            handle: { breadcrumb: "Adjustment History" },
                            async lazy() {
                              const { AdjustmentHistory } = await import(
                                "@redotech/redo-merchant-inventory-app/adjustment-history/adjustment-history"
                              );
                              return { Component: AdjustmentHistory };
                            },
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: "consigned-product-families/:productFamilyId",
            handle: { breadcrumb: productFamilyDetailBreadcrumb },
            async lazy() {
              const { ProductFamilyView } = await import(
                "@redotech/redo-merchant-inventory-app/product-families/product-family-view"
              );
              return {
                element: (
                  <RedoOutletAccessGuard>
                    <ProductFamilyView />
                  </RedoOutletAccessGuard>
                ),
              };
            },
          },

          {
            path: "consignment",
            handle: { breadcrumb: "Consignment" },
            async lazy() {
              const { CatalogConsignmentProducts } = await import(
                "@redotech/redo-merchant-inventory-app/catalog-consignment/catalog-consignment-products"
              );
              return {
                Component: () => (
                  <RedoOutletAccessGuard>
                    <CatalogConsignmentProducts />
                  </RedoOutletAccessGuard>
                ),
              };
            },
          },
        ],
      },
      {
        path: "pricing-rules",
        handle: { breadcrumb: "Pricing Rules" },
        async lazy() {
          const { PricingRules } = await import(
            "@redotech/redo-merchant-inventory-app/pricing-rules/pricing-rules"
          );
          return { Component: PricingRules };
        },
      },
      {
        path: "adjustments",
        handle: { breadcrumb: "Inventory Adjustments" },
        async lazy() {
          const { InventoryAdjustments } = await import(
            "@redotech/redo-merchant-inventory-app/adjustments/inventory-adjustments"
          );
          return { Component: InventoryAdjustments };
        },
      },
      {
        path: "reports",
        handle: { breadcrumb: "Reports" },
        async lazy() {
          const { InventoryReporting } = await import(
            "@redotech/redo-merchant-inventory-app/reporting/inventory-reporting"
          );
          return { Component: InventoryReporting };
        },
      },
      {
        path: "forecasting",
        handle: { breadcrumb: "Forecasting" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: ":viewName",
            handle: { breadcrumb: forecastingViewBreadcrumb },
            async lazy() {
              const { ForecastingWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/forecasting/forecasting"
              );
              return {
                element: (
                  <ForecastingWrapper breadcrumb={forecastingViewBreadcrumb} />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ForecastingPage } = await import(
                    "@redotech/redo-merchant-inventory-app/forecasting/forecasting"
                  );
                  return { Component: ForecastingPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "scenario-planning",
        handle: { breadcrumb: "Scenario Planning" },
        children: [
          {
            index: true,
            Component: () => (
              <ScenarioPlanningAccessGuard>
                <Navigate replace to={`new-${Date.now()}`} />
              </ScenarioPlanningAccessGuard>
            ),
          },
          {
            path: ":scenarioId",
            async lazy() {
              const { ScenarioPlanningDetailPage } = await import(
                "@redotech/redo-merchant-inventory-app/scenario-planning/scenario-planning-detail"
              );
              return {
                element: (
                  <ScenarioPlanningAccessGuard>
                    <ScenarioPlanningDetailPage />
                  </ScenarioPlanningAccessGuard>
                ),
              };
            },
          },
        ],
      },
      {
        path: "suppliers",
        handle: { breadcrumb: "Suppliers" },
        children: [
          {
            index: true,
            async lazy() {
              const { SuppliersPage } = await import(
                "@redotech/redo-merchant-inventory-app/suppliers/suppliers"
              );
              return { Component: SuppliersPage };
            },
          },
          {
            path: ":supplierId",
            handle: { breadcrumb: supplierDetailsBreadcrumb },
            async lazy() {
              const { SupplierDetailsPage } = await import(
                "@redotech/redo-merchant-inventory-app/suppliers/supplier-detail"
              );
              return {
                element: (
                  <SupplierDetailsPage breadcrumb={supplierDetailsBreadcrumb} />
                ),
              };
            },
          },
        ],
      },
      {
        path: "bill-of-materials",
        handle: { breadcrumb: "Bill of Materials" },
        children: [
          {
            index: true,
            async lazy() {
              const { BillOfMaterialsListPage } = await import(
                "@redotech/redo-merchant-inventory-app/bill-of-materials/bill-of-materials-list"
              );
              return { Component: BillOfMaterialsListPage };
            },
          },
          {
            path: "new",
            handle: { breadcrumb: "New" },
            async lazy() {
              const { BillOfMaterialsDetailPage } = await import(
                "@redotech/redo-merchant-inventory-app/bill-of-materials/bill-of-materials-detail"
              );
              return {
                element: (
                  <BillOfMaterialsDetailPage
                    breadcrumb={billOfMaterialsDetailBreadcrumb}
                  />
                ),
              };
            },
          },
          {
            path: ":billOfMaterialsId",
            handle: { breadcrumb: billOfMaterialsDetailBreadcrumb },
            async lazy() {
              const { BillOfMaterialsDetailPage } = await import(
                "@redotech/redo-merchant-inventory-app/bill-of-materials/bill-of-materials-detail"
              );
              return {
                element: (
                  <BillOfMaterialsDetailPage
                    breadcrumb={billOfMaterialsDetailBreadcrumb}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "transfers",
        handle: { breadcrumb: "Transfers" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: ":viewName",
            handle: { breadcrumb: transferViewBreadcrumb },
            async lazy() {
              const { TransfersWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/transfers/transfers-list"
              );
              return {
                element: (
                  <TransfersWrapper breadcrumb={transferViewBreadcrumb} />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { TransfersListPage } = await import(
                    "@redotech/redo-merchant-inventory-app/transfers/transfers-list"
                  );
                  return { Component: TransfersListPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "purchase-orders",
        handle: { breadcrumb: "Purchase Orders" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: ":viewName",
            handle: { breadcrumb: purchaseOrderViewBreadcrumb },
            async lazy() {
              const { PurchaseOrdersWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders"
              );
              return {
                element: (
                  <PurchaseOrdersWrapper
                    breadcrumb={purchaseOrderViewBreadcrumb}
                  />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PurchaseOrdersPage } = await import(
                    "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-orders"
                  );
                  return { Component: PurchaseOrdersPage };
                },
              },
              {
                path: ":purchaseOrderId",
                handle: { breadcrumb: purchaseOrderBreadcrumb },
                async lazy() {
                  const { PurchaseOrderPage } = await import(
                    "@redotech/redo-merchant-inventory-app/purchase-orders/purchase-order-detail"
                  );
                  return {
                    element: (
                      <PurchaseOrderPage breadcrumb={purchaseOrderBreadcrumb} />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "shipments",
        handle: { breadcrumb: "Shipments" },
        children: [
          { index: true, element: <Navigate replace to="all" /> },
          {
            path: "damaged",
            handle: { breadcrumb: "Damaged" },
            async lazy() {
              const { DisputesPage } = await import(
                "@redotech/redo-merchant-inventory-app/disputes/disputes"
              );
              return { Component: DisputesPage };
            },
          },
          {
            path: ":viewName",
            handle: { breadcrumb: shipmentViewBreadcrumb },
            async lazy() {
              const { ShipmentsWrapper } = await import(
                "@redotech/redo-merchant-inventory-app/shipments/shipments"
              );
              return {
                element: (
                  <ShipmentsWrapper breadcrumb={shipmentViewBreadcrumb} />
                ),
              };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { ShipmentsPage } = await import(
                    "@redotech/redo-merchant-inventory-app/shipments/shipments"
                  );
                  return { Component: ShipmentsPage };
                },
              },
              {
                path: ":shipmentId",
                handle: { breadcrumb: shipmentDetailBreadcrumb },
                async lazy() {
                  const { ShipmentDetailPage } = await import(
                    "@redotech/redo-merchant-inventory-app/shipments/shipment-detail"
                  );
                  return {
                    element: (
                      <ShipmentDetailPage
                        breadcrumb={shipmentDetailBreadcrumb}
                      />
                    ),
                  };
                },
              },
            ],
          },
        ],
      },
      {
        path: "liquidation/dashboard",
        handle: { breadcrumb: "Home" },
        async lazy() {
          const { LiquidationPage } = await import(
            "@redotech/redo-merchant-inventory-app/liquidation/liquidation-page"
          );
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <LiquidationPage />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
      {
        path: "liquidation/products",
        handle: { breadcrumb: "All Listings" },
        async lazy() {
          const { MarketplaceListings } = await import(
            "@redotech/redo-merchant-inventory-app/marketplace/marketplace-listings"
          );
          return {
            Component: () => (
              <MarketplaceAccessGuard>
                <MarketplaceListings />
              </MarketplaceAccessGuard>
            ),
          };
        },
      },
      // temporary route for printing barcodes for ASRV excess inventory
      {
        path: "print-barcode",
        handle: { breadcrumb: "Print Barcode" },
        async lazy() {
          const { PrintBarcodePage } = await import(
            "@redotech/redo-merchant-inventory-app/print-barcode/print-barcode-page"
          );
          return {
            Component: () => (
              <RedoOutletAccessGuard>
                <PrintBarcodePage />
              </RedoOutletAccessGuard>
            ),
          };
        },
      },
    ],
  },
];

export const MarketplaceAccessGuard = memo(function MarketplaceAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;

  if (!isMarketplaceEnabled) {
    // Redirect to main inventory page if marketplace is not enabled
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const RedoOutletAccessGuard = memo(function RedoOutletAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  // only available to the Redo Outlet team
  if (team._id !== "68800d96229f8fc10f4dc1ce") {
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const ReFulfillmentAccessGuard = memo(function ReFulfillmentAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isReFulfillmentEnabled = team.settings.reFulfillment?.enabled ?? false;

  if (!isReFulfillmentEnabled) {
    return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
  }

  return <>{children}</>;
});

export const ScenarioPlanningAccessGuard = memo(
  function ScenarioPlanningAccessGuard({ children }: { children: ReactNode }) {
    const routes = useRequiredContext(MerchantRoutesContext);
    const user = useContext(UserContext);

    const isRedoEmployee =
      user?.email?.endsWith("@redo.com") ||
      user?.email?.endsWith("@getredo.com");

    if (!isRedoEmployee) {
      return <Navigate replace to={routes.inventoryManagementBaseUrl} />;
    }

    return <>{children}</>;
  },
);
